import React from 'react';

import { MytaverseLogoIcon } from '@icons';
import { ICustomStyles, IEventThemeTypes } from '@interfaces';
import DefaultLoadingBackground from '@public/images/LoadingTutorialBg.png';
import { colors, COLORS } from '@constants/colors';
import { getClientSpecificPageBackgrounds } from '@helpers/clientSpecificUtils';

const [
  DefaultBackground,
  DefaultBackgroundMobile,
  DefaultDevicesTestMicrophoneBackground,
  DefaultDevicesTestMicrophoneBackgroundMobile,
  DefaultDevicesTestSpeakerBackground,
  DefaultDevicesTestSpeakerBackgroundMobile,
  DefaultDevicesTestConnectionBackground,
  DefaultDevicesTestConnectionBackgroundMobile,
  DefaultDevicesTestWebcamBackground,
  DefaultDevicesTestWebcamBackgroundMobile,
  DefaultAvatarBackground,
  DefaultAvatarBackgroundMobile,
  NewCustomerLogo,
  NewCustomerAltLogo,
] = getClientSpecificPageBackgrounds();

// should be supported
// customerLogo
// loginPage.components.background
// loginPage.assets.loginButtonBg
// loginPage.assets.loginButtonBgHover
// loginPage.assets.filledButtonColor
// loginPage.assets.helperTextColor
// loginPage.assets.helperTextLinkColor
export const customAppThemes = {
  light: ({
    eventName,
    backgroundImage,
    backgroundImageMobile,
    loginButtonBackgroundColor,
    loginButtonBackgroundColorHover,
    helperTextColor,
    helperTextLinkColor,
    submitButtonBackgroundColor,
    submitButtonBackgroundColorHover,
    customerLogo,
  }: ICustomStyles) => {
    const defaultImg = backgroundImage?.url ?? DefaultBackground;

    return {
      themeName: IEventThemeTypes.LIGHT,
      customerLogoFromAdmin: customerLogo?.url, // this will be the correct one to use
      customerLogo: NewCustomerLogo ?? customerLogo?.url, // we are using this one until we can upload new logo in admin
      customerAltLogo: NewCustomerAltLogo ?? customerLogo?.url, // we are using this one until we can upload new logo in admin
      loadingBackground: backgroundImage?.url ?? DefaultLoadingBackground,
      playBackground: defaultImg,
      newDefaultBackground: defaultImg,
      newDefaultBackgroundMobile:
        backgroundImageMobile?.url ?? DefaultBackgroundMobile ?? defaultImg,
      DevicesTestMicrophoneBackground:
        DefaultDevicesTestMicrophoneBackground ?? defaultImg,
      DevicesTestMicrophoneBackgroundMobile:
        DefaultDevicesTestMicrophoneBackgroundMobile ?? defaultImg,
      DevicesTestSpeakerBackground:
        DefaultDevicesTestSpeakerBackground ?? defaultImg,
      DevicesTestSpeakerBackgroundMobile:
        DefaultDevicesTestSpeakerBackgroundMobile ?? defaultImg,
      DevicesTestConnectionBackground:
        DefaultDevicesTestConnectionBackground ?? defaultImg,
      DevicesTestConnectionBackgroundMobile:
        DefaultDevicesTestConnectionBackgroundMobile ?? defaultImg,
      DevicesTestWebcamBackground:
        DefaultDevicesTestWebcamBackground ?? defaultImg,
      DevicesTestWebcamBackgroundMobile:
        DefaultDevicesTestWebcamBackgroundMobile ?? defaultImg,
      AvatarSelectionBackground: DefaultAvatarBackground ?? defaultImg,
      AvatarSelectionBackgroundMobile:
        DefaultAvatarBackgroundMobile ?? defaultImg,
      mainColor: submitButtonBackgroundColor ?? COLORS.MYTAVERSE_BLUE,
      submitButton: {
        backgroundColor: submitButtonBackgroundColor ?? COLORS.MYTAVERSE_BLUE,
        backgroundColorHover:
          submitButtonBackgroundColorHover ?? COLORS.MYTAVERSE_BLUE,
      },
      loginPage: {
        typography: {
          title: eventName,
        },
        components: {
          withRobot: true,
          background: backgroundImage?.url,
          withVideo: false,
        },
        assets: {
          loginButtonBg: loginButtonBackgroundColor,
          loginButtonBgHover: loginButtonBackgroundColorHover,
          filledButtonColor: COLORS.LOGIN_LIGHT_GREY,

          titleColor: helperTextColor ?? COLORS.ST_PATRICKS,
          subtitleColor: helperTextColor ?? COLORS.NEW_BLACK,
          helperTextColor: helperTextColor ?? COLORS.GREY,
          helperTextLinkColor: helperTextLinkColor,
          helperTextMT: 6,
          imageOpacity: '',
          formBoxShadow: `5px 5px 18px rgba(0, 0, 0, 0.2), -31px -30px 15px -20px ${COLORS.WHITE}`,
        },
      },
      languageButton: {
        assets: {
          iconColor: colors.oxford_60,
          fontColor: colors.oxford,
        },
      },
      avatarPage: {
        assets: {
          buttonTextColor: COLORS.BLACK,
        },
      },
      eventDropdown: {
        assets: {
          fontColor: COLORS.AVATAR_BLACK,
        },
      },
      loadingPage: {
        mainColor: submitButtonBackgroundColor ?? colors.cyan,
      },
    };
  },

  dark: ({
    eventName,
    backgroundImage,
    backgroundImageMobile,
    loginButtonBackgroundColor,
    loginButtonBackgroundColorHover,
    helperTextColor,
    helperTextLinkColor,
    submitButtonBackgroundColor,
    submitButtonBackgroundColorHover,
    customerLogo,
  }: ICustomStyles) => {
    const defaultImg = backgroundImage?.url ?? DefaultBackground;

    return {
      themeName: IEventThemeTypes.DARK,
      customerLogoFromAdmin: customerLogo?.url, // this will be the correct one to use
      customerLogo: NewCustomerLogo ?? customerLogo?.url, // we are using this one until we can uplaod new logo in admin
      customerAltLogo: NewCustomerAltLogo ?? customerLogo?.url, // we are using this one until we can upload new logo in admin
      loadingBackground: backgroundImage?.url ?? DefaultLoadingBackground,
      playBackground: defaultImg,
      newDefaultBackground: defaultImg,
      newDefaultBackgroundMobile:
        backgroundImageMobile?.url ?? DefaultBackgroundMobile ?? defaultImg,
      DevicesTestMicrophoneBackground:
        DefaultDevicesTestMicrophoneBackground ?? defaultImg,
      DevicesTestMicrophoneBackgroundMobile:
        DefaultDevicesTestMicrophoneBackgroundMobile ?? defaultImg,
      DevicesTestSpeakerBackground:
        DefaultDevicesTestSpeakerBackground ?? defaultImg,
      DevicesTestSpeakerBackgroundMobile:
        DefaultDevicesTestSpeakerBackgroundMobile ?? defaultImg,
      DevicesTestConnectionBackground:
        DefaultDevicesTestConnectionBackground ?? defaultImg,
      DevicesTestConnectionBackgroundMobile:
        DefaultDevicesTestConnectionBackgroundMobile ?? defaultImg,
      DevicesTestWebcamBackground:
        DefaultDevicesTestWebcamBackground ?? defaultImg,
      DevicesTestWebcamBackgroundMobile:
        DefaultDevicesTestWebcamBackgroundMobile ?? defaultImg,
      AvatarSelectionBackground: DefaultAvatarBackground ?? defaultImg,
      AvatarSelectionBackgroundMobile:
        DefaultAvatarBackgroundMobile ?? defaultImg,
      mainColor: submitButtonBackgroundColor ?? COLORS.MYTAVERSE_BLUE,
      submitButton: {
        backgroundColor: submitButtonBackgroundColor ?? COLORS.MYTAVERSE_BLUE,
        backgroundColorHover:
          submitButtonBackgroundColorHover ?? COLORS.MYTAVERSE_BLUE,
      },
      loginPage: {
        typography: {
          title: eventName,
        },
        components: {
          withRobot: false,
          background: backgroundImage?.url,
          withVideo: false,
        },
        assets: {
          loginButtonBg: loginButtonBackgroundColor,
          loginButtonBgHover: loginButtonBackgroundColorHover,
          filledButtonColor: COLORS.LOGIN_LIGHT_GREY,
          titleColor: helperTextColor ?? COLORS.WHITE,
          subtitleColor: helperTextColor ?? COLORS.WHITE,
          helperTextColor: helperTextColor ?? COLORS.WHITE,
          helperTextLinkColor: helperTextLinkColor,
          helperTextMT: 4,
          formBoxShadow: 'unset',
        },
      },
      languageButton: {
        assets: {
          iconColor: colors.white,
          fontColor: colors.white,
        },
      },
      avatarPage: {
        assets: {
          buttonTextColor: COLORS.WHITE,
        },
      },
      eventDropdown: {
        assets: {
          fontColor: COLORS.AVATAR_BLACK,
        },
      },
    };
  },

  default: () => ({
    themeName: IEventThemeTypes.DEFAULT,
    customerLogoFromAdmin: <MytaverseLogoIcon />, // this will be the correct one to use
    customerLogo: NewCustomerLogo, // we are using this one until we can uplaod new logo in admin
    customerAltLogo: NewCustomerAltLogo, // we are using this one until we can upload new logo in admin
    loadingBackground: DefaultLoadingBackground,
    playBackground: DefaultBackground,
    newDefaultBackground: DefaultBackground,
    newDefaultBackgroundMobile: DefaultBackgroundMobile ?? DefaultBackground,
    DevicesTestMicrophoneBackground:
      DefaultDevicesTestMicrophoneBackground ?? DefaultBackground,
    DevicesTestMicrophoneBackgroundMobile:
      DefaultDevicesTestMicrophoneBackgroundMobile ?? DefaultBackground,
    DevicesTestSpeakerBackground:
      DefaultDevicesTestSpeakerBackground ?? DefaultBackground,
    DevicesTestSpeakerBackgroundMobile:
      DefaultDevicesTestSpeakerBackgroundMobile ?? DefaultBackground,
    DevicesTestConnectionBackground:
      DefaultDevicesTestConnectionBackground ?? DefaultBackground,
    DevicesTestConnectionBackgroundMobile:
      DefaultDevicesTestConnectionBackgroundMobile ?? DefaultBackground,
    DevicesTestWebcamBackground:
      DefaultDevicesTestWebcamBackground ?? DefaultBackground,
    DevicesTestWebcamBackgroundMobile:
      DefaultDevicesTestWebcamBackgroundMobile ?? DefaultBackground,
    AvatarSelectionBackground: DefaultAvatarBackground ?? DefaultBackground,
    AvatarSelectionBackgroundMobile:
      DefaultAvatarBackgroundMobile ?? DefaultBackground,
    mainColor: COLORS.MYTAVERSE_BLUE,
    submitButton: {
      backgroundColor: COLORS.MYTAVERSE_BLUE,
      backgroundColorHover: COLORS.MYTAVERSE_BLUE,
    },
    loginPage: {
      typography: {
        title: 'login.pageTitle',
      },
      components: {
        withRobot: true,
        background: DefaultBackground,
        withVideo: false,
      },
      assets: {
        loginButtonBg: colors.myta,
        loginButtonBgHover: colors.myta,
        filledButtonColor: COLORS.LOGIN_LIGHT_GREY,

        titleColor: colors.oxford,
        subtitleColor: colors.oxford_60,
        helperTextColor: COLORS.DARK_GREY,
        helperTextLinkColor: COLORS.GREY,
        helperTextMT: 6,
        imageOpacity: '',
        formBoxShadow: `5px 5px 18px rgba(0, 0, 0, 0.2), -31px -30px 15px -20px ${COLORS.WHITE}`,
      },
    },
    languageButton: {
      assets: {
        iconColor: colors.oxford_60,
        fontColor: colors.oxford,
      },
    },
    avatarPage: {
      assets: {
        buttonTextColor: COLORS.BLACK,
      },
    },
    eventDropdown: {
      assets: {
        fontColor: colors.oxford_60,
      },
    },
    loadingPage: {
      mainColor: colors.cyan,
    },
  }),
};
export { IEventThemeTypes };
